import { Button } from '@heroui/react';

import { AGENT_TIME_OPTIONS } from '../../constants';

import i18n from '~/common/helpers/i18n';
import {
  ContentHeader,
  ContentHeaderInput,
  TimeRangeWithTabs,
  useSearchOnEnter,
  type TimeRange
} from '~/components/ContentHeader';
import { useRetentionDateLimits } from '~/scenes/Monitor/components';

export type AgentLogsFiltersProps = {
  pipelineName: string;
  agentName: string;
  content: string;
  timeRange: TimeRange;
  resetToDefault: boolean;
  retentionValue?: number;
  onPipelineNameChange: (value: string) => void;
  onAgentNameChange: (value: string) => void;
  onContentChange: (value: string) => void;
  onTimeRangeChange: (timeRange: TimeRange) => void;
  onSearch: () => void;
  onClear: () => void;
};

export function AgentLogsFilters({
  pipelineName,
  agentName,
  content,
  timeRange,
  retentionValue,
  onPipelineNameChange,
  onAgentNameChange,
  onContentChange,
  onTimeRangeChange,
  onSearch,
  onClear
}: AgentLogsFiltersProps) {
  const handleKeyDown = useSearchOnEnter(onSearch);
  const { minDate, maxDate } = useRetentionDateLimits(retentionValue);

  return (
    <ContentHeader className='flex-col gap-5 border-b border-outline'>
      <div className='grid flex-1 gap-4 grid-cols-[repeat(auto-fit,minmax(348px,1fr))] w-full'>
        <ContentHeaderInput
          name='pipelineName'
          variant='labeled'
          label={i18n.t('scenes.monitor.agentLogs.filters.pipeline')!}
          placeholder={i18n.t('scenes.pipeline_logs.guidance.name')!}
          value={pipelineName}
          onChange={onPipelineNameChange}
          onKeyDown={handleKeyDown}
          data-testid='monitor-agent-logs-filter-pipeline'
        />

        <ContentHeaderInput
          name='agentName'
          variant='labeled'
          label={i18n.t('scenes.monitor.agentLogs.filters.agentName')!}
          placeholder={i18n.t('scenes.monitor.agentLogs.filters.agentName')!}
          value={agentName}
          onChange={onAgentNameChange}
          onKeyDown={handleKeyDown}
          data-testid='monitor-agent-logs-filter-agent-name'
        />

        <ContentHeaderInput
          name='content'
          variant='labeled'
          label={i18n.t('scenes.monitor.agentLogs.filters.content')!}
          placeholder={i18n.t('scenes.pipeline_logs.guidance.log_message')!}
          value={content}
          onChange={onContentChange}
          onKeyDown={handleKeyDown}
          data-testid='monitor-agent-logs-filter-content'
        />
      </div>

      <div className='flex justify-between w-full'>
        <div className='flex items-center gap-1.5'>
          <TimeRangeWithTabs
            value={timeRange}
            onChange={onTimeRangeChange}
            minDate={minDate}
            maxDate={maxDate}
            timeOptions={AGENT_TIME_OPTIONS}
            testIdPrefix='monitor-agent-logs'
          />
        </div>

        <div className='flex items-center gap-1.5'>
          <Button
            color='primary'
            variant='solid'
            onPress={onSearch}
            data-testid='monitor-agent-logs-filter-search'
            size='sm'
          >
            {i18n.t('scenes.monitor.agentLogs.filters.search')}
          </Button>

          <Button
            color='primary'
            variant='bordered'
            onPress={onClear}
            data-testid='monitor-agent-logs-filter-clear'
            size='sm'
          >
            {i18n.t('scenes.monitor.agentLogs.filters.clear')}
          </Button>
        </div>
      </div>
    </ContentHeader>
  );
}
